import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const CHART_IMG =
  "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwZmluYW5jaWFsJTIwY2hhcnR8ZW58MHx8fHwxNzg3NjQxMzI4fDA&ixlib=rb-4.1.0&q=85";
const CRYPTO_IMG =
  "https://images.pexels.com/photos/7267491/pexels-photo-7267491.jpeg?auto=compress&cs=tinysrgb&w=1200";
const BULLION_IMG =
  "https://images.pexels.com/photos/8442325/pexels-photo-8442325.jpeg?auto=compress&cs=tinysrgb&w=1200";
const FOREX_IMG =
  "https://images.pexels.com/photos/19878432/pexels-photo-19878432.jpeg?auto=compress&cs=tinysrgb&w=1200";

const chapters = [
  {
    n: "01",
    title: "Crypto",
    tickers: "BTC · ETH · SOL +22",
    copy: "The top twenty-five digital assets, streamed tick by tick into a single private book.",
    span: "md:col-span-7",
    img: CRYPTO_IMG,
    tab: "crypto",
  },
  {
    n: "02",
    title: "Mega-Caps",
    tickers: "AAPL · MSFT · NVDA +22",
    copy: "Twenty-five verified companies that move the world index, quoted beside your coins.",
    span: "md:col-span-5",
    img: CHART_IMG,
    tab: "stocks",
  },
  {
    n: "03",
    title: "Precious Metals",
    tickers: "Gold · Silver · Pt +17",
    copy: "Twenty metals — bullion benchmarks and the strategic complex, priced in real time.",
    span: "md:col-span-5",
    img: BULLION_IMG,
    tab: "metals",
  },
  {
    n: "04",
    title: "Global Forex",
    tickers: "EUR · GBP · JPY +9",
    copy: "Twelve major currency pairs around the clock, five days a week, without leaving the terminal.",
    span: "md:col-span-7",
    img: FOREX_IMG,
    tab: "forex",
  },
];

export default function Chapters() {
  return (
    <section data-testid="chapters-section" className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
      >
        <div>
          <p className="mb-4 text-[10px] uppercase tracking-[0.3em] text-gold">
            The Manifesto
          </p>
          <h2 className="font-serif text-5xl font-light tracking-tighter text-white sm:text-6xl">
            Four arenas.
            <br />
            <span className="italic text-gold">One terminal.</span>
          </h2>
        </div>
        <p className="max-w-xs text-sm leading-relaxed text-white/50">
          Aureon tracks every market that matters — numbered, ordered, and
          always open.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
        {chapters.map((c, i) => (
          <motion.div
            key={c.n}
            data-testid={`chapter-card-${c.n}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className={`group relative flex min-h-[280px] flex-col justify-between overflow-hidden border border-white/10 bg-surface p-8 transition-colors duration-500 hover:border-gold/50 ${c.span}`}
          >
            <Link to={`/markets?tab=${c.tab}`} data-testid={`chapter-link-${c.tab}`} className="absolute inset-0 z-20" aria-label={`Open ${c.title} markets`} />
            {c.img && (
              <>
                <img
                  src={c.img}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover opacity-30 transition-opacity duration-700 group-hover:opacity-45"
                />
                <div className="absolute inset-0 bg-black/40" />
              </>
            )}
            <div className="relative z-10 flex items-start justify-between">
              <span className="font-serif text-6xl font-light text-stroke-gold transition-colors duration-500 group-hover:text-gold group-hover:[-webkit-text-stroke:0px]">
                {c.n}
              </span>
              <ArrowUpRight className="h-5 w-5 text-white/30 transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-gold" />
            </div>
            <div className="relative z-10">
              <p className="mb-2 text-[10px] uppercase tracking-[0.3em] text-gold/80">
                {c.tickers}
              </p>
              <h3 className="font-serif text-3xl font-light tracking-tight text-white">
                {c.title}
              </h3>
              <p className="mt-3 max-w-sm text-xs leading-relaxed text-white/50">
                {c.copy}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
